/* eslint-disable react/no-unescaped-entities */
import { Calendar, CheckCircle2, Snowflake, Sun, AlertTriangle, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import GermanyResourceShell from './_components/GermanyResourceShell';

const DESC = 'Germany university intakes 2026 — Winter vs Summer intake comparison, application deadlines calendar, programme availability, and a 12-month prep checklist.';

const INTAKE_COMPARISON = [
  { feature: 'Semester start',           winter: 'October',                    summer: 'April' },
  { feature: 'Application deadline',     winter: 'Mid-July',                   summer: 'Mid-January' },
  { feature: 'Programmes available',     winter: '90% (almost all programmes)', summer: '40% (limited subset)' },
  { feature: 'Best for Bachelors',       winter: 'Yes — full programme list', summer: 'Limited — only major universities' },
  { feature: 'Best for Masters',         winter: 'Yes — primary intake',       summer: 'Some Masters only' },
  { feature: 'Scholarship deadlines',    winter: 'Earlier (Oct–Dec prev. yr)', summer: 'Earlier (May–July prev. yr)' },
  { feature: 'Visa appointment',         winter: 'Apply by June (1 mo prior)', summer: 'Apply by Dec (1 mo prior)' },
  { feature: 'Blocked account opening',  winter: 'May–June',                   summer: 'Nov–Dec' },
  { feature: 'Accommodation deadline',   winter: 'Apr–May',                    summer: 'Oct–Nov (prev. yr)' },
  { feature: 'Job market on graduation', winter: 'Easier (April grad cycle)',  summer: 'Standard (Oct grad cycle)' },
];

const CALENDAR_WINTER = [
  { month: 'Sept–Oct (prev. yr)',  action: 'Begin shortlisting universities and programmes. Take IELTS / TOEFL / TestDaF.' },
  { month: 'Nov–Dec (prev. yr)',   action: 'Start APS Certificate process (4–8 weeks). Apply for DAAD / scholarship deadlines.' },
  { month: 'Jan–Mar',              action: 'Submit university applications via Uni-Assist / direct. Deadlines vary by university — generally Jan-31 to Apr-30.' },
  { month: 'Apr–May',              action: 'Receive admission letters (Zulassung). Confirm acceptance + book Studentenwerk slot.' },
  { month: 'Jun',                  action: 'Open blocked account (€11,904). Buy travel insurance. Apply for student visa.' },
  { month: 'Jul–Aug',              action: 'Receive visa. Book flights. Finalise accommodation. Wire deposit for housing.' },
  { month: 'Sep',                  action: 'Travel to Germany. Anmeldung within 14 days. Enroll at university. Semester starts Oct 1.' },
];

const CALENDAR_SUMMER = [
  { month: 'Mar–Apr (prev. yr)',   action: 'Shortlist universities offering Summer intake. Take IELTS / TOEFL / TestDaF.' },
  { month: 'May–Jun',              action: 'APS Certificate process. Scholarship applications (some Summer-specific cycles).' },
  { month: 'Jul–Sep',              action: 'Submit university applications. Deadlines: Jul-15 to Oct-15 (mid-Sep is most common).' },
  { month: 'Oct–Nov',              action: 'Receive admission letters. Confirm acceptance + Studentenwerk booking.' },
  { month: 'Dec',                  action: 'Open blocked account. Apply for student visa (4–8 weeks processing).' },
  { month: 'Jan–Feb',              action: 'Visa issued. Finalise accommodation. Wire deposits.' },
  { month: 'Mar',                  action: 'Travel to Germany. Anmeldung within 14 days. Semester starts Apr 1.' },
];

const CHECKLIST = [
  'English / German language certificate (IELTS / TOEFL / TestDaF / DSH)',
  'APS Certificate (mandatory for Indians)',
  'Bachelor degree + final marksheet',
  'CV in German format (Lebenslauf)',
  'Motivation letter / Statement of Purpose',
  'Letters of recommendation (2 academic + 1 professional)',
  'Project portfolio (for design / engineering / arts programmes)',
  'Translated + apostilled school certificates (10th + 12th)',
  '€75–€150 application fee per university',
];

const FAQS = [
  { q: 'When is the main intake for German universities?',
    a: 'The Winter Semester (October start) is the primary intake — 90%+ of all programmes accept applications for this cycle. The Summer Semester (April start) is secondary with ~40% of programmes available. Most Indian students target the Winter intake because of broader programme selection, better scholarship alignment, and an easier post-graduation job-market cycle (April graduation = peak hiring in May–June).' },
  { q: 'What are the application deadlines for Winter intake 2026?',
    a: 'Most German universities use July 15 as the standard cutoff for Winter Semester applications. Exceptions: TU Munich, RWTH Aachen, KIT, and Heidelberg often close earlier (May 31). For non-EU applicants like Indians, Uni-Assist deadlines are typically 4 weeks before the university\'s direct deadline (around June 15–30). Start applying by January–March to avoid the rush.' },
  { q: 'Is the Summer intake worth applying for?',
    a: 'Yes, if (a) you missed the Winter cycle and don\'t want to lose a year, OR (b) your specific programme is offered in Summer (check the DAAD International Programmes Database). Top universities like TU Munich, RWTH Aachen, and Heidelberg do offer Summer Masters in CS, EE, and select STEM fields. The downside: fewer scholarship cycles align with Summer intake, and your post-grad job hunt falls in October — a slightly weaker hiring window than May–June.' },
  { q: 'How early should I start the application process?',
    a: '14 months before semester start. For October 2027 (Winter Semester): start in August 2026 with language tests + university shortlisting. Submit applications by Jan–Mar 2027. Receive admissions by Apr–May 2027. Visa filing by June 2027. Travel September 2027. The single biggest reason for Indian applicants missing intakes is starting 6 months late on APS Certificate or scholarship applications.' },
  { q: 'Can I switch from Winter to Summer intake after acceptance?',
    a: 'Most German universities allow a one-time intake deferral within the same academic year — but you must request it in writing within 30 days of acceptance. Some highly competitive programmes (TU Munich CS, Heidelberg Medicine) refuse switches and require re-applying. Always confirm the deferral policy in your acceptance letter before declining a Winter offer in hopes of getting Summer.' },
  { q: 'What is Uni-Assist and do I have to apply through it?',
    a: 'Uni-Assist is a central application clearinghouse used by ~180 German public universities for international applicants. It verifies your foreign credentials before forwarding your application to the actual university. Application fee: €75 for the first university + €30 for each additional. Some top universities (TU Munich, RWTH Aachen, LMU Munich) bypass Uni-Assist and accept direct applications — saving you 4–6 weeks of processing time. Always check each university\'s application page to know which route applies.' },
];

const GermanyIntakesPage = () => (
  <GermanyResourceShell
    slug="intakes"
    breadcrumbLabel="Intakes"
    articleHeadline="Germany University Intakes 2026 — Winter vs Summer Application Guide"
    articleDescription={DESC}
    faqs={FAQS}
    related={['public-universities', 'student-visa', 'student-accommodation']}
  >
    {/* Hero */}
    <section className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #003087 0%, #001F5C 60%, #020617 100%)' }}>
      <div className="relative max-w-7xl mx-auto px-6 py-16 lg:py-20">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5 mb-5">
            <Calendar className="w-3.5 h-3.5 text-[#C5A028]" />
            <span className="text-xs text-white/90 font-medium">Germany Resource — Intakes</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-syne text-white leading-tight mb-4">
            Germany University Intakes 2026 — Winter vs Summer Compared
          </h1>
          <p className="text-base lg:text-lg text-slate-300 leading-relaxed mb-6">
            German universities operate on two semester intakes: Winter (October) and Summer (April). Choosing the right intake decides 80% of your overseas-education timeline — from APS to visa to housing. This is the complete 2026 intake guide — head-to-head comparison, application deadline calendars for both cycles, and the 9-item document checklist that works.
          </p>
          <div className="grid grid-cols-3 gap-3 max-w-xl">
            <div className="bg-white/5 border border-white/10 rounded-xl p-3">
              <div className="text-[10px] text-slate-400 uppercase tracking-wider mb-0.5">Winter</div>
              <div className="text-base font-bold text-[#C5A028]">Oct (Primary)</div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-3">
              <div className="text-[10px] text-slate-400 uppercase tracking-wider mb-0.5">Summer</div>
              <div className="text-base font-bold text-[#C5A028]">Apr (Limited)</div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-3">
              <div className="text-[10px] text-slate-400 uppercase tracking-wider mb-0.5">Plan-Ahead</div>
              <div className="text-base font-bold text-[#C5A028]">14 months</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Comparison Table */}
    <section className="py-14 lg:py-18 bg-white">
      <div className="max-w-5xl mx-auto px-6">
        <h2 className="text-2xl lg:text-3xl font-bold font-syne text-[#003087] mb-2">Winter vs Summer — Head-to-Head</h2>
        <p className="text-sm text-slate-600 mb-6">Both intakes use the same admission process, language requirements, and APS Certificate. The differences below decide WHICH intake works for your timeline.</p>
        <div className="overflow-x-auto bg-slate-50 border border-slate-200 rounded-xl">
          <table className="w-full text-sm">
            <thead className="bg-[#003087] text-white">
              <tr>
                <th className="px-4 py-3 text-left font-syne">Factor</th>
                <th className="px-4 py-3 text-left font-syne"><Snowflake className="w-3.5 h-3.5 inline mr-1.5" />Winter Intake</th>
                <th className="px-4 py-3 text-left font-syne"><Sun className="w-3.5 h-3.5 inline mr-1.5" />Summer Intake</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 bg-white">
              {INTAKE_COMPARISON.map((c) => (
                <tr key={c.feature}>
                  <td className="px-4 py-3 font-semibold text-[#003087]">{c.feature}</td>
                  <td className="px-4 py-3 text-slate-700">{c.winter}</td>
                  <td className="px-4 py-3 text-slate-700">{c.summer}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>

    {/* Calendars */}
    <section className="py-14 lg:py-18 bg-slate-50">
      <div className="max-w-6xl mx-auto px-6">
        <h2 className="text-2xl lg:text-3xl font-bold font-syne text-[#003087] mb-5">Application Deadline Calendars</h2>
        <div className="grid lg:grid-cols-2 gap-5">
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <Snowflake className="w-5 h-5 text-blue-600" />
              <h3 className="text-lg font-bold font-syne text-[#003087]">Winter Semester — October Start</h3>
            </div>
            <ol className="space-y-3 text-sm">
              {CALENDAR_WINTER.map((c, i) => (
                <li key={i} className="flex gap-3">
                  <span className="font-bold text-[#C5A028] shrink-0 w-32">{c.month}</span>
                  <span className="text-slate-700 leading-relaxed">{c.action}</span>
                </li>
              ))}
            </ol>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <Sun className="w-5 h-5 text-amber-600" />
              <h3 className="text-lg font-bold font-syne text-[#003087]">Summer Semester — April Start</h3>
            </div>
            <ol className="space-y-3 text-sm">
              {CALENDAR_SUMMER.map((c, i) => (
                <li key={i} className="flex gap-3">
                  <span className="font-bold text-[#C5A028] shrink-0 w-32">{c.month}</span>
                  <span className="text-slate-700 leading-relaxed">{c.action}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </section>

    {/* Checklist */}
    <section className="py-14 lg:py-18 bg-white">
      <div className="max-w-4xl mx-auto px-6">
        <h2 className="text-2xl lg:text-3xl font-bold font-syne text-[#003087] mb-5">9-Item Application Checklist</h2>
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-6">
          <ul className="space-y-3">
            {CHECKLIST.map((c, i) => (
              <li key={i} className="flex items-start gap-3 text-[15px] text-slate-700">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                <span>{c}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>

    {/* Programme-Type Intake Differences */}
    <section className="py-14 lg:py-18 bg-slate-50">
      <div className="max-w-5xl mx-auto px-6">
        <h2 className="text-2xl lg:text-3xl font-bold font-syne text-[#003087] mb-3">When the Standard Intake Calendar Does NOT Apply</h2>
        <p className="text-[15px] leading-7 text-slate-700 mb-6">
          The Winter/Summer cycle above covers ~85% of German university applications. The remaining 15% — DAAD-funded scholarships, double-degree partnerships, PhD positions, and rolling-admission private universities — run on independent deadlines. Verify the specific intake rules for your programme type BEFORE finalising your calendar.
        </p>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <h3 className="text-base font-bold font-syne text-[#003087] mb-2">DAAD-Funded Programmes</h3>
            <p className="text-sm text-slate-700 leading-relaxed">
              DAAD-funded Masters (e.g. Public Policy, Development Studies, Energy Engineering) operate on a single annual cycle — typically October-only, with applications closing 12 months in advance (October–December of the prior year). Summer entry is rarely an option.
            </p>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <h3 className="text-base font-bold font-syne text-[#003087] mb-2">Double-Degree (Erasmus Mundus, ITN)</h3>
            <p className="text-sm text-slate-700 leading-relaxed">
              Joint programmes between German + one other EU university follow the lead-institution calendar. Apply 14 months before intake. These are highly competitive (3–7% acceptance rates) but offer full funding for Indian applicants.
            </p>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <h3 className="text-base font-bold font-syne text-[#003087] mb-2">PhD Positions</h3>
            <p className="text-sm text-slate-700 leading-relaxed">
              PhDs are advertised individually by research groups — no fixed semester intake. Most start dates fall in April or October but can begin any month. The application flow is direct-to-professor (not via Uni-Assist). Timeline: 3–6 months from inquiry to formal admission.
            </p>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <h3 className="text-base font-bold font-syne text-[#003087] mb-2">Private Universities (IU, GISMA, EBS)</h3>
            <p className="text-sm text-slate-700 leading-relaxed">
              Private universities run rolling admissions — apply any month, decisions in 4–6 weeks. Many offer 3–4 intakes per year (Jan, Apr, Jul, Oct). Best for students who missed the public-university Winter deadline and don't want to lose a year.
            </p>
          </div>
        </div>
      </div>
    </section>

    {/* FAQ */}
    <section className="py-14 lg:py-18 bg-slate-50">
      <div className="max-w-4xl mx-auto px-6">
        <h2 className="text-2xl lg:text-3xl font-bold font-syne text-[#003087] mb-7">Frequently Asked Questions</h2>
        <div className="space-y-3">
          {FAQS.map((f, i) => (
            <details key={i} className="group bg-white border border-slate-200 rounded-xl p-5">
              <summary className="font-syne font-semibold text-slate-900 cursor-pointer text-[15px] flex items-start justify-between">
                <span className="pr-6">{f.q}</span>
                <span className="text-[#003087] group-open:rotate-45 transition-transform text-xl leading-none">+</span>
              </summary>
              <p className="mt-3 text-sm leading-7 text-slate-700">{f.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  </GermanyResourceShell>
);

export default GermanyIntakesPage;
